import React from 'react';
import EVSEChecklist from './EVSEChecklist';
import EVSEForm from './EVSEForm';
import {
  PRICE_COMPONENT_REQUIRED_FIELDS, PRICE_COMPONENT_FIELD_GROUPS,
  GRACE_PERIOD_OPTIONS
} from '../config/tariffFields';

const IconPlus = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconTrash = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconCheck = () => (
  <svg width="12" height="12" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function TariffStepPricing({
  priceComponents, setPriceComponents,
  includeIdleFee, setIncludeIdleFee,
  idleFee, setIdleFee
}) {

  const addComponent = () => {
    setPriceComponents(prev => [
      ...prev,
      {
        tempId: Date.now(),
        formData: {},
        selectedFields: [],
        errors: {}
      }
    ]);
  };

  const removeComponent = (tempId) => {
    setPriceComponents(prev => prev.filter(c => c.tempId !== tempId));
  };

  const updateComponent = (tempId, updates) => {
    setPriceComponents(prev => prev.map(c => c.tempId === tempId ? { ...c, ...updates } : c));
  };

  const toggleComponentField = (tempId, name) => {
    const component = priceComponents.find(c => c.tempId === tempId);
    const isSelected = component.selectedFields.includes(name);
    const newSelectedFields = isSelected
      ? component.selectedFields.filter(f => f !== name)
      : [...component.selectedFields, name];

    const newFormData = { ...component.formData };
    if (isSelected) delete newFormData[name];

    updateComponent(tempId, { selectedFields: newSelectedFields, formData: newFormData });
  };

  const selectAllInGroup = (tempId, names) => {
    const component = priceComponents.find(c => c.tempId === tempId);
    updateComponent(tempId, {
      selectedFields: Array.from(new Set([...component.selectedFields, ...names]))
    });
  };

  const clearAllInGroup = (tempId, names) => {
    const component = priceComponents.find(c => c.tempId === tempId);
    const newFormData = { ...component.formData };
    names.forEach(n => delete newFormData[n]);
    updateComponent(tempId, {
      selectedFields: component.selectedFields.filter(f => !names.includes(f)),
      formData: newFormData
    });
  };

  const handleFieldChange = (tempId, name, value) => {
    const component = priceComponents.find(c => c.tempId === tempId);
    const newErrors = { ...component.errors };
    if (newErrors[name]) delete newErrors[name];
    updateComponent(tempId, {
      formData: { ...component.formData, [name]: value },
      errors: newErrors
    });
  };

  const updateIdleFee = (field, value) => {
    setIdleFee(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 18 }}>Add Price Components</h1>
      </div>

      {priceComponents.length === 0 && (
        <div className="card empty-state" style={{ marginBottom: 16 }}>
          <h3>No price components added yet</h3>
          <p>Add at least one price component to define how this tariff charges.</p>
        </div>
      )}

      {priceComponents.map((component, index) => (
        <div className="entity-card" key={component.tempId}>
          <div className="entity-card-header">
            <span className="entity-card-title">
              Price Component {index + 1}
              {component.formData.charging_unit && (
                <span className="entity-card-badge">{component.formData.charging_unit}</span>
              )}
            </span>
            {priceComponents.length > 1 && (
              <button
                type="button"
                className="entity-remove-btn"
                onClick={() => removeComponent(component.tempId)}
              >
                <IconTrash />
              </button>
            )}
          </div>

          <div className="evse-builder">
            <EVSEChecklist
              groups={PRICE_COMPONENT_FIELD_GROUPS}
              selectedFields={component.selectedFields}
              onToggle={(name) => toggleComponentField(component.tempId, name)}
              onSelectAllInGroup={(names) => selectAllInGroup(component.tempId, names)}
              onClearAllInGroup={(names) => clearAllInGroup(component.tempId, names)}
            />
            <EVSEForm
              title={`Price Component ${index + 1} Details`}
              requiredFields={PRICE_COMPONENT_REQUIRED_FIELDS}
              groups={PRICE_COMPONENT_FIELD_GROUPS}
              selectedFields={component.selectedFields}
              formData={component.formData}
              onChange={(name, value) => handleFieldChange(component.tempId, name, value)}
              errors={component.errors}
              emptyHint="Tick a box on the left to add tax, minimum charge, or billing increment rules."
            />
          </div>
        </div>
      ))}

      <button type="button" className="add-entity-btn" onClick={addComponent}>
        <IconPlus /> Add Price Component
      </button>

      <div
        className={`checklist-item ${includeIdleFee ? 'checked' : ''}`}
        style={{ maxWidth: 340, marginBottom: includeIdleFee ? 16 : 20 }}
        onClick={() => setIncludeIdleFee(!includeIdleFee)}
      >
        <div className="checklist-checkbox">
          <IconCheck />
        </div>
        <span className="checklist-label">Include Idle / Parking Fee</span>
      </div>

      {includeIdleFee && (
        <div className="idle-fee-card dynamic-field-enter">
          <div className="idle-fee-title">Idle / Parking Fee</div>
          <div className="idle-fee-grid">
            <div className="form-group">
              <label>Idle Time</label>
              <input
                type="text"
                placeholder="Idle Time"
                value={idleFee.idle_time || ''}
                onChange={(e) => updateIdleFee('idle_time', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Idle Price</label>
              <input
                type="text"
                placeholder="Idle Price"
                value={idleFee.idle_price || ''}
                onChange={(e) => updateIdleFee('idle_price', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Select Grace Period</label>
              <select
                value={idleFee.grace_period || ''}
                onChange={(e) => updateIdleFee('grace_period', e.target.value)}
              >
                <option value="">Select Grace Period</option>
                {GRACE_PERIOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}
    </>
  );
}